/**
 * Escolha dentro de um conjunto fechado (signos, séries, tipos de página,
 * cartões da home). Com `ordenavel`, a ordem em que os itens foram escolhidos é
 * a ordem da tela e pode ser mudada com Subir e Descer.
 */
import { Button } from '@/components/atoms';

import { mover } from './helpers';
import styles from './Portal.module.css';

interface Opcao<T extends string> {
  valor: T;
  rotulo: string;
  detalhe?: string;
}

interface Props<T extends string> {
  rotulo: string;
  opcoes: Opcao<T>[];
  selecionados: T[];
  onChange: (selecionados: T[]) => void;
  ordenavel?: boolean;
  dica?: string;
  erro?: string | null;
}

export function EditorSelecao<T extends string>({
  rotulo,
  opcoes,
  selecionados,
  onChange,
  ordenavel = false,
  dica,
  erro,
}: Props<T>) {
  const alternar = (valor: T) =>
    onChange(
      selecionados.includes(valor)
        ? selecionados.filter((v) => v !== valor)
        : [...selecionados, valor],
    );
  const nomeDe = (v: T) => opcoes.find((o) => o.valor === v)?.rotulo ?? v;

  return (
    <fieldset style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}>
      <legend className={styles.sub2}>
        {rotulo} ({selecionados.length} de {opcoes.length})
      </legend>
      {dica && <p className={styles.dica}>{dica}</p>}
      <div className={styles.grade}>
        {opcoes.map((o) => (
          <label key={o.valor} className={styles.interruptor}>
            <input
              type="checkbox"
              checked={selecionados.includes(o.valor)}
              onChange={() => alternar(o.valor)}
            />
            <span>
              {o.rotulo}
              {o.detalhe && <span className={styles.linhaMeta}> {o.detalhe}</span>}
            </span>
          </label>
        ))}
      </div>
      {ordenavel && selecionados.length > 1 && (
        <ol
          className={styles.lista}
          aria-label={`Ordem: ${rotulo}`}
          style={{ marginTop: 'var(--sp-2)' }}
        >
          {selecionados.map((v, i) => (
            <li key={v} className={styles.linha}>
              <span>
                <span className={styles.ordem}>{i + 1}</span>
                <span className={styles.linhaTitulo}>{nomeDe(v)}</span>
              </span>
              <div className={styles.linhaAcoes}>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={i === 0}
                  onClick={() => onChange(mover(selecionados, i, i - 1))}
                  aria-label={`Subir ${nomeDe(v)}`}
                >
                  Subir
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={i === selecionados.length - 1}
                  onClick={() => onChange(mover(selecionados, i, i + 1))}
                  aria-label={`Descer ${nomeDe(v)}`}
                >
                  Descer
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}
      {erro && <span className={styles.erroCampo}>{erro}</span>}
    </fieldset>
  );
}
